import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PersonalTask } from '../../api/personal-tasks';

interface PersonalTasksState {
  loading: boolean;
  error: string | null;
  data: PersonalTask[];
  current: PersonalTask | null;
  deleting: boolean;
  updating: boolean;
  currentError: string | null;
  creating: boolean;
  createError: string | null;
}

const INITIAL_STATE: PersonalTasksState = {
  loading: false,
  error: null,
  data: [],
  current: null,
  deleting: false,
  updating: false,
  currentError: null,
  creating: false,
  createError: null,
};

const updateCase = (
  state: PersonalTasksState,
  { payload }: PayloadAction<PersonalTask>,
): PersonalTasksState => ({
  ...state,
  current: payload,
  updating: true,
});

const requestComplete = (state: PersonalTasksState): PersonalTasksState => ({
  ...state,
  updating: false,
  current: null,
});

export interface UpdatePayload {
  id: number;
  data: Partial<PersonalTask>;
}

export const personalTasksSlice = createSlice({
  initialState: INITIAL_STATE,
  name: 'personalTasks',
  reducers: {
    fetchStart: (state: PersonalTasksState) => ({ ...state, loading: true }),
    fetchSuccess: (state, { payload }: PayloadAction<PersonalTask[]>) => ({
      ...state,
      data: payload,
      loading: false,
    }),
    createStart: (state: PersonalTasksState) => ({
      ...state,
      creating: true,
    }),
    create: (
      state: PersonalTasksState,
      { payload }: PayloadAction<PersonalTask>,
    ) => ({
      ...state,
      data: [payload, ...state.data],
    }),
    createSuccess: (state: PersonalTasksState) => ({
      ...state,
      creating: false,
      createError: null,
    }),
    createFailure: (state: PersonalTasksState) => ({
      ...state,
      creating: false,
      createError: 'error',
    }),
    deleteStart: (
      state: PersonalTasksState,
      { payload }: PayloadAction<PersonalTask>,
    ) => ({
      ...state,
      current: payload,
      deleting: true,
    }),
    deleteSuccess: (state, { payload }: PayloadAction<PersonalTask>) => ({
      ...state,
      data: [
        ...state.data.filter(
          (d) => d.personalTaskId !== payload.personalTaskId,
        ),
      ],
      current: null,
      deleting: false,
    }),
    deleteFailed: (state: PersonalTasksState) => ({
      ...state,
      current: null,
      deleting: false,
    }),
    updateStart: updateCase,
    update: (
      state: PersonalTasksState,
      { payload }: PayloadAction<UpdatePayload>,
    ): PersonalTasksState => ({
      ...state,
      data: [...state.data].reduce((arr: PersonalTask[], next) => {
        let task = { ...next };

        if (task.personalTaskId === payload.id) {
          task = Object.assign(task, payload.data);
        }

        return [...arr, task];
      }, []),
      updating: false,
      current: null,
    }),
    updateSuccess: requestComplete,
    updateFailed: requestComplete,
  },
});
